/**
 * SurveyPage — public landing page for emailed magic-link survey tokens.
 *
 * Accessible via /survey?token=<32-hex-char token> (no authentication required).
 *
 * On mount it calls GET /api/rest/app/v1/survey/redeem?token=… to validate and redeem
 * the token. On success it renders the CustomerFeedbackModal inline. On failure
 * (expired/used/missing token) it shows a friendly error message.
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { CustomerFeedbackModal } from '@/components/feedback/CustomerFeedbackModal';
import { httpClient } from '@/utils/httpClient';

type RedeemState =
  | { status: 'loading' }
  | { status: 'ready'; transactionLogId: string }
  | { status: 'expired' }
  | { status: 'error'; message: string };

export const SurveyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? '').trim();

  const [redeemState, setRedeemState] = useState<RedeemState>({ status: 'loading' });
  const [open, setOpen] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setRedeemState({ status: 'expired' });
      return;
    }

    const redeem = async () => {
      try {
        const data = await httpClient.get<{ transactionLogId: string }>(
          `/api/rest/app/v1/survey/redeem?token=${encodeURIComponent(token)}`,
          { skipAuth: true },
        );
        if (data?.transactionLogId) {
          setRedeemState({ status: 'ready', transactionLogId: data.transactionLogId });
        } else {
          setRedeemState({ status: 'expired' });
        }
      } catch (err) {
        // HTTP 410 → expired/used, anything else → error
        if (
          err &&
          typeof err === 'object' &&
          'status' in err &&
          (err as { status: number }).status === 410
        ) {
          setRedeemState({ status: 'expired' });
        } else {
          const message = err instanceof Error ? err.message : 'Unable to open this survey link.';
          setRedeemState({ status: 'error', message });
        }
      }
    };

    void redeem();
  }, [token]);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {submitted ? (
          <>
            <Typography variant="h5" gutterBottom>
              Thank you!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your survey response has been recorded.
            </Typography>
          </>
        ) : redeemState.status === 'loading' ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Opening your survey…
            </Typography>
          </Box>
        ) : redeemState.status === 'expired' ? (
          <>
            <Typography variant="h5" gutterBottom>
              Link expired or already used
            </Typography>
            <Alert severity="warning" sx={{ textAlign: 'left', mt: 2 }}>
              This survey link has expired or was already used. Survey links are valid for 7 days
              and can only be used once. Please contact your equipment room for assistance.
            </Alert>
          </>
        ) : redeemState.status === 'error' ? (
          <>
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'left', mt: 2 }}>
              {redeemState.message}
            </Alert>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              Customer Service Survey
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please take a moment to complete your feedback form.
            </Typography>
            <CustomerFeedbackModal
              open={open}
              transactionLogId={redeemState.transactionLogId}
              onClose={() => setOpen(false)}
              onSubmitSuccess={() => {
                setSubmitted(true);
                setOpen(false);
              }}
            />
          </>
        )}
      </Paper>
    </Container>
  );
};
